// Standalone public report status frame (DS-5789).
//
// Served at /tools/1/reports/<key>/ (backend-api streams this file from the
// frontend bucket at that URL; the dev stack rewrites it in nginx) and embedded
// in third-party pages by public/report-embed.js, so it must stay tiny: this
// entry is plain DOM code with no framework imports at all (Vue lives in the
// shared chunk-vendors bundle, which this page must not pull in).
//
// The frame is fully static: the report key comes from the path and the view
// mode from the query string — exactly the URL contract the legacy embeds
// rely on. Data comes from the unauthenticated public report API.
import './report-frame.css';

// The status windows shown by the default (rows) view, matching the legacy
// public frame: last hour / last day / last week.
const ROWS_WINDOWS = [
  { minutes: 60, label: 'Last Hour' },
  { minutes: 1440, label: 'Last Day' },
  { minutes: 10080, label: 'Last Week' },
];

const STATUS_SYMBOLS = {
  pass: '✔', // check mark
  warn: '⚠', // warning triangle
  fail: '✖', // cross
  nodata: '?',
};

function intParam(params, name, fallback = 0) {
  const value = parseInt(params.get(name), 10);
  return Number.isNaN(value) ? fallback : value;
}

function parseContext() {
  // The live URL contract is /tools/1/reports/<key>/ (backend-api serves this
  // page at that URL); ?report=<key> is a fallback so the page can be
  // exercised in development at /report-frame.html directly.
  const match = window.location.pathname.match(/\/tools\/1\/reports\/([^/]+)/);
  const params = new URLSearchParams(window.location.search);
  const reportId = params.get('report') || (match ? match[1] : '');
  const grid = Math.min(12, intParam(params, 'grid'));
  let minutes = intParam(params, 'minutes');
  if (grid > 0 && minutes <= 0) {
    minutes = 15;
  }
  let mode = 'rows';
  if (grid > 0) {
    mode = 'grid';
  } else if (minutes > 0) {
    mode = 'minutes';
  }
  return {
    reportId,
    minutes,
    grid,
    mode,
    windows: mode === 'rows' ? ROWS_WINDOWS : [{ minutes, label: `Last ${minutes} minutes` }],
  };
}

const ctx = parseContext();
const state = {
  report: null,
  notPublic: false,
  // "<callId>|<locationId>|<minutes>" -> { stat, name } (stat null on error)
  statuses: {},
  // callId -> CASC score (0-1000)
  cascScores: {},
};

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(String(response.status));
  }
  return response.json();
}

function reportRows() {
  const rows = [];
  (state.report ? state.report.groups : []).forEach((group) => {
    group.rows.forEach((row) => {
      rows.push({ callId: row.call_id, locationId: row.location_id || '__ALL__' });
    });
  });
  return rows;
}

async function fetchStatuses() {
  const fetches = [];
  const seen = new Set();
  reportRows().forEach(({ callId, locationId }) => {
    ctx.windows.forEach(({ minutes }) => {
      const key = `${callId}|${locationId}|${minutes}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      const url =
        `/api/2/reports/${ctx.reportId}/public/${callId}/stats` +
        `?minutes=${minutes}&location_id=${encodeURIComponent(locationId)}`;
      fetches.push(
        fetchJson(url)
          .then((data) => {
            state.statuses[key] = { stat: data.stat, name: data.meta.name };
          })
          .catch(() => {
            state.statuses[key] = { stat: null };
          }),
      );
    });
  });
  await Promise.all(fetches);
}

async function fetchCascScores() {
  const callIds = new Set(reportRows().map(({ callId }) => callId));
  await Promise.all(
    [...callIds].map((callId) =>
      fetchJson(`/api/2/reports/${ctx.reportId}/public/${callId}/insights?weekly=true`)
        .then((data) => {
          if (data && (data.score || data.score === 0)) {
            state.cascScores[callId] = data.score;
          }
        })
        .catch(() => {}),
    ),
  );
}

// ---------------------------------------------------------------------------
// Rendering (textContent throughout: report and call names are user data)
// ---------------------------------------------------------------------------

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

function callName(callId) {
  const found = Object.entries(state.statuses).find(
    ([key, value]) => key.startsWith(`${callId}|`) && value.name,
  );
  return found ? found[1].name : '…';
}

function statusFor(callId, locationId, minutes) {
  const entry = state.statuses[`${callId}|${locationId}|${minutes}`];
  if (!entry || !entry.stat) {
    return 'nodata';
  }
  if (entry.stat.failure > 0) {
    return 'fail';
  }
  if (entry.stat.warning > 0) {
    return 'warn';
  }
  return entry.stat.pass > 0 ? 'pass' : 'nodata';
}

function cascText(callId) {
  const score = state.cascScores[callId];
  return score || score === 0 ? (score / 100).toFixed(2) : '-';
}

function renderGrid(group) {
  const grid = el('div', 'grid');
  group.rows.forEach((row) => {
    const locationId = row.location_id || '__ALL__';
    const status = statusFor(row.call_id, locationId, ctx.minutes);
    const tile = el('div', `grid-tile bg-${status}`);
    // Legacy grid tiles were col-xs-<grid> wide (grid twelfths of the row).
    tile.style.width = `${(ctx.grid / 12) * 100}%`;
    tile.appendChild(el('div', 'grid-call-name', callName(row.call_id)));
    tile.appendChild(el('div', 'grid-casc', `CASC ${cascText(row.call_id)}`));
    tile.appendChild(el('div', 'grid-status', STATUS_SYMBOLS[status]));
    grid.appendChild(tile);
  });
  return grid;
}

function renderTable(group) {
  const table = el('table', 'calls');
  const head = table.createTHead().insertRow();
  head.appendChild(el('th', 'name-col', 'Name'));
  head.appendChild(el('th', '', 'CASC Score'));
  ctx.windows.forEach(({ label }) => head.appendChild(el('th', '', label)));

  const body = table.createTBody();
  group.rows.forEach((row) => {
    const locationId = row.location_id || '__ALL__';
    const tr = body.insertRow();
    tr.appendChild(el('td', 'name-col', callName(row.call_id)));
    tr.appendChild(el('td', 'casc', cascText(row.call_id)));
    ctx.windows.forEach(({ minutes }) => {
      const status = statusFor(row.call_id, locationId, minutes);
      tr.appendChild(el('td', `status fg-${status}`, STATUS_SYMBOLS[status]));
    });
  });
  return table;
}

function postHeight() {
  // The embed script (report-embed.js) resizes the iframe from this message;
  // the target origin is the embedding page's origin, taken from the referrer
  // as the legacy frame did server-side via the Referer header.
  if (window.parent === window) {
    return;
  }
  let target = '*';
  try {
    target = new URL(document.referrer).origin || '*';
  } catch (ex) {
    target = '*';
  }
  window.parent.postMessage(document.body.scrollHeight, target);
}

function render() {
  const root = document.getElementById('app');
  root.textContent = '';
  const frame = el('div', 'report-frame');
  root.appendChild(frame);

  if (state.notPublic) {
    const message = el(
      'div',
      'not-public',
      'Sorry, this report is not publicly available. Create your own API performance reports with ',
    );
    const link = el('a', '', 'APImetrics');
    link.href = 'https://apimetrics.io';
    link.rel = 'external';
    message.appendChild(link);
    message.appendChild(document.createTextNode('.'));
    frame.appendChild(message);
    postHeight();
    return;
  }

  if (!state.report) {
    frame.appendChild(el('div', 'loading', 'Loading…'));
    postHeight();
    return;
  }

  if (state.report.meta.branding) {
    const logoLink = el('a', 'logo');
    logoLink.href = 'https://apimetrics.io/';
    logoLink.target = '_blank';
    logoLink.rel = 'external noopener';
    const logo = el('img');
    logo.src = '/img/apimetrics-logo-medium.png';
    logo.alt = 'Intelligent API monitoring';
    logoLink.appendChild(logo);
    frame.appendChild(logoLink);
  }
  frame.appendChild(el('h2', 'report-name', state.report.meta.name));

  state.report.groups.forEach((group) => {
    const section = el('section');
    section.appendChild(el('h3', 'group-name', group.name));
    section.appendChild(ctx.mode === 'grid' ? renderGrid(group) : renderTable(group));
    frame.appendChild(section);
  });

  postHeight();
}

// ---------------------------------------------------------------------------
// Startup + rolling refresh
// ---------------------------------------------------------------------------

async function refreshData() {
  await Promise.all([fetchStatuses(), fetchCascScores()]);
  render();
}

async function main() {
  render();
  try {
    state.report = await fetchJson(`/api/2/reports/${ctx.reportId}/public/`);
  } catch (ex) {
    state.notPublic = true;
    render();
    return;
  }
  document.title = `${state.report.meta.name} - APImetrics`;
  render();
  await refreshData();

  // The legacy frame reloaded the whole page every ceil(minutes / 15) minutes
  // in the rolling-window modes; refetch on the same cadence instead.
  if (ctx.mode !== 'rows') {
    setInterval(refreshData, 60000 * Math.ceil(ctx.minutes / 15));
  }
}

main();
